<template>
  <section class="menus-settings settings-grid">
    <slot name="menu"></slot>

    <div
      ref="container"
      class="settings-grid__main custom-scrollbar"
    >
      <div class="settings-grid__main__header">
        <slot name="title"></slot>

        <button
          class="btn btn-secondary btn-icon"
          :disabled="isViewerUser"
          @click="
            addGlobalData({
              baseGlobalData: {
                module: 'GlobalMenu',
                name: 'Menu Global',
              },
            })
          "
        >
          <IconPlus size="xs" />
          <span> Criar novo menu </span>
        </button>
      </div>

      <Loader
        v-if="loading"
        color="#3E9AA6"
      />
      <div
        v-else
        class="menus-list"
      >
        <ul class="menus-settings__list">
          <li
            v-for="(menu, menuId) in menus"
            :key="menuId"
          >
            <IconModule />
            <h3>{{ menu.title }}</h3>
            <IconLanguage
              v-if="menu.language"
              :language="menu.language"
            />
            <button @click.stop="toggleActionMenu(menuId)">
              <span></span>
              <span></span>
              <span></span>
            </button>
            <ul
              v-show="openedActionMenu === menuId"
              :ref="menuId"
              v-clickaway="closeActionMenu"
              class="menu-actions"
            >
              <li>
                <button @click.stop="editGlobalData({ globalId: menuId })">
                  Editar menu
                </button>
              </li>
              <li>
                <button
                  :disabled="isViewerUser"
                  @click.stop="addGlobalData({ baseGlobalData: menu })"
                >
                  Duplicar menu
                </button>
              </li>
              <li>
                <button
                  class="text-danger"
                  :disabled="isViewerUser"
                  @click.stop="menuBeingDeleted = menuId"
                >
                  Deletar menu
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <div
        v-if="!Object.keys(menus).length"
        class="menus-settings__empty-state"
      >
        Você ainda não criou nenhum menu 😿
      </div>
    </div>

    <div class="menus-settings__actions settings-grid__actions d-flex"></div>

    <InstanceProperties v-if="activeGlobalData" />

    <div
      v-show="menuBeingDeleted"
      class="remove-menu-modal"
      @click.self="menuBeingDeleted = null"
    >
      <div class="remove-menu-modal__content">
        <div class="remove-menu-modal__content__header">
          <label
            class="btn-close"
            @click="menuBeingDeleted = null"
          >
            <IconClose />
          </label>
          <IconDelete
            class="text-danger"
            size="lg"
          />
          <h2>Atenção</h2>
        </div>

        <div class="remove-menu-modal__content__text">
          <p>
            Ao excluir, você perde todas as edições.
            <br />Todas as páginas com este menu ficarão sem nenhum. <br />Tem
            certeza que deseja excluir este menu?
          </p>
        </div>

        <div class="remove-menu-modal__content__buttons">
          <button
            class="btn btn-secondary btn-white"
            type="button"
            @click="menuBeingDeleted = null"
          >
            Não, cancelar
          </button>
          <button
            class="btn btn-secondary"
            type="submit"
            @click="removeMenu"
          >
            Sim, quero excluir
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

import Loader from '@/components/Loader.vue';
import InstanceProperties from '@/components/InstanceProperties.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import IconModule from '@/components/icons/IconModule.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconDelete from '@/components/icons/IconDelete.vue';
import IconPlus from '@/components/icons/IconPlus.vue';

export default {
  name: 'MenusSettings',
  components: {
    InstanceProperties,
    IconLanguage,
    IconModule,
    IconClose,
    IconDelete,
    IconPlus,
    Loader,
  },
  directives: {
    // clickaway taken from https://stackoverflow.com/questions/36170425/detect-click-outside-element
    clickaway: {
      bind(el, binding, vnode) {
        // eslint-disable-next-line no-param-reassign
        el.clickOutsideEvent = (event) => {
          // here I check that click was outside the el and his children
          if (!(el === event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      openedActionMenu: null,
      menuBeingDeleted: null,
    };
  },
  computed: {
    ...mapState(['loading', 'activeGlobalData']),
    ...mapGetters(['menus', 'isViewerUser']),
  },
  mounted() {
    this.setDropdownsPosition();
    this.$refs.container.addEventListener(
      'scroll',
      debounce(this.setDropdownsPosition, 150),
    );
  },
  methods: {
    closeActionMenu() {
      this.openedActionMenu = null;
    },
    toggleActionMenu(menuId) {
      if (this.openedActionMenu === menuId) {
        this.openedActionMenu = null;
      } else {
        this.openedActionMenu = menuId;
      }
    },
    removeMenu() {
      this.deleteGlobalData({ globalId: this.menuBeingDeleted }).then(() => {
        this.menuBeingDeleted = null;
      });
    },
    setDropdownsPosition() {
      const dropdowns = this.$el.querySelectorAll('.menu-actions');

      dropdowns.forEach((dropdown) => {
        const position = dropdown.closest('li').getBoundingClientRect();

        const dropdownHeight = 28 + 35 * dropdown.childElementCount;
        const elementPosition = position.bottom + dropdownHeight;
        const viewportEdge =
          (window.innerHeight || document.documentElement.clientHeight) - 100;

        if (elementPosition > viewportEdge) {
          dropdown.setAttribute('drop-up', true);
        } else {
          dropdown.removeAttribute('drop-up');
        }
      });
    },
    ...mapActions(['editGlobalData', 'addGlobalData', 'deleteGlobalData']),
  },
};
</script>

<style lang="scss" scoped>
.menus-settings {
  &__list {
    button {
      outline: none;
      border: none;
      background: none;
    }

    > li {
      padding: 12px 16px;
      position: relative;
      display: flex;
      align-items: center;
      border-bottom: 1px solid rgba($lb-light-gray, 0.5);

      &:first-child {
        border-top: 1px solid rgba($lb-light-gray, 0.5);
      }

      .icon {
        margin-right: 15px;
      }

      h3 {
        margin-right: auto;
        font-size: $size-small;
      }

      > button {
        span {
          @include is-rounded;

          width: 4px;
          height: 4px;
          background: $crm-blue-primary;
          display: inline-block;
          margin: 0 2px;
        }
      }

      .menu-actions {
        background-color: $lb-white;
        box-shadow: 4px 4px 25px rgba($lb-black, 0.25);
        position: absolute;
        top: 100%;
        right: 0;
        z-index: 1;
        padding: 1em 0;

        &[drop-up] {
          top: auto;
          bottom: 100%;
        }

        button {
          padding: 0.5em 1.5em;
          cursor: pointer;
          width: 100%;
          text-align: left;

          &[disabled] {
            opacity: 0.5;
          }

          &:hover {
            background-color: $lb-ice;
          }
        }
      }
    }
  }

  &::v-deep {
    .instance-properties.instance-properties--full {
      height: 100vh;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
  }

  &__empty-state {
    margin: auto;
    font-size: $size-normal;
  }

  .remove-menu-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;

    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;

        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 40px 0 20px 0;
        }

        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        p {
          font-size: $size-normal;
          line-height: 26px;
          color: $gray-900;
          margin-bottom: 20px;
        }
      }

      &__buttons {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        width: 70%;
      }
    }
  }
}
</style>
